import { CONFIG } from '../config';
import { Item } from '../entities/Item';
import type { ItemType } from '../types';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

interface SoundManagerLike {
    playItemSpawnSound(): void;
}

interface CanvasSize {
    width: number;
    height: number;
    isLandscape: boolean;
}

/**
 * Item 엔티티 풀
 */
class ItemEntityPool {
    private pool: Item[] = [];
    private maxPoolSize: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 20 : 50;

    get(x: number, y: number, type: ItemType): Item {
        const item = this.pool.pop();
        if (item) {
            item.reset(x, y, type);
            return item;
        }
        return new Item(x, y, type);
    }

    release(item: Item): void {
        if (this.pool.length < this.maxPoolSize) {
            this.pool.push(item);
        }
    }

    clear(): void {
        this.pool = [];
    }
}

export class ItemSpawner {
    private soundManager: SoundManagerLike | null;
    private items: Item[];
    private itemPool: ItemEntityPool;
    private timeSinceLastSpawn: number;
    private nextSpawnTime: number;
    private renderer: RendererLike | null;

    constructor(soundManager: SoundManagerLike | null = null) {
        this.soundManager = soundManager;
        this.items = [];
        this.itemPool = new ItemEntityPool();
        this.timeSinceLastSpawn = 0;
        this.nextSpawnTime = this.getRandomSpawnTime();
        this.renderer = null;
    }

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    getCanvasSize(): CanvasSize {
        if (this.renderer) {
            return {
                width: this.renderer.getCanvasWidth(),
                height: this.renderer.getCanvasHeight(),
                isLandscape: this.renderer.isLandscape
            };
        }
        return {
            width: CONFIG.CANVAS_WIDTH,
            height: CONFIG.CANVAS_HEIGHT,
            isLandscape: false
        };
    }

    reset(): void {
        // 모든 아이템을 풀로 반환
        for (const item of this.items) {
            this.itemPool.release(item);
        }
        this.items = [];
        this.timeSinceLastSpawn = 0;
        this.nextSpawnTime = this.getRandomSpawnTime();
    }

    getRandomSpawnTime(): number {
        // 5초 ~ 15초 사이 랜덤
        return 5 + Math.random() * 10;
    }

    /**
     * 설정된 확률에 따라 랜덤 아이템 타입 반환
     */
    getRandomItemType(): ItemType {
        const spawn = CONFIG.ITEM_SPAWN;
        const rand = Math.random();

        let cumulative = 0;

        cumulative += spawn.HEART;
        if (rand < cumulative) return 'heart';

        cumulative += spawn.STAR;
        if (rand < cumulative) return 'star';

        cumulative += spawn.LIGHTNING;
        if (rand < cumulative) return 'lightning';

        cumulative += spawn.CLOCK;
        if (rand < cumulative) return 'clock';

        cumulative += spawn.BOMB;
        if (rand < cumulative) return 'bomb';

        return 'magnet';
    }

    update(deltaTime: number): void {
        this.timeSinceLastSpawn += deltaTime;

        // 랜덤한 시간에 아이템 생성
        if (this.timeSinceLastSpawn >= this.nextSpawnTime) {
            this.spawnItem();
            this.timeSinceLastSpawn = 0;
            this.nextSpawnTime = this.getRandomSpawnTime();
        }

        // 아이템 업데이트 (swap-and-pop 패턴)
        let writeIdx = 0;
        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            item.update(deltaTime);

            if (item.isAlive) {
                this.items[writeIdx++] = item;
            } else {
                this.itemPool.release(item);
            }
        }
        this.items.length = writeIdx;
    }

    spawnItem(): void {
        const { width, height, isLandscape } = this.getCanvasSize();
        const margin = CONFIG.PLAY_AREA.MARGIN;
        let minX: number, maxX: number, minY: number, maxY: number;

        if (isLandscape) {
            // Landscape: 좌우 20% 영역을 제외하고 중앙 60% 영역에서만 스폰
            const playWidth = width - margin * 2;
            minX = margin + playWidth * 0.2;
            maxX = margin + playWidth * 0.8;
            minY = margin + 20;
            maxY = height - margin - 20;
        } else {
            // Portrait: 상단/하단 20% 영역을 제외하고 중앙 60% 영역에서만 스폰
            minX = margin + 20;
            maxX = width - margin - 20;
            const playHeight = height - margin * 2;
            minY = margin + playHeight * 0.2;
            maxY = margin + playHeight * 0.8;
        }

        // 랜덤 위치
        const x = minX + Math.random() * (maxX - minX);
        const y = minY + Math.random() * (maxY - minY);

        // 랜덤 타입 (CONFIG에 정의된 확률에 따라)
        const type = this.getRandomItemType();

        this.items.push(this.itemPool.get(x, y, type));

        // Play spawn sound
        if (this.soundManager) {
            this.soundManager.playItemSpawnSound();
        }
    }

    getItems(): Item[] {
        return this.items;
    }

    removeItem(item: Item): void {
        // isAlive를 false로 설정하면 다음 update에서 배열에서 제거됨
        // 이렇게 해야 ItemAdapter.sync()가 Graphics를 해제할 수 있음
        item.isAlive = false;
    }

    transformPositionsOnOrientationChange(wasLandscape: boolean, isLandscape: boolean): void {
        // Get old and new canvas sizes
        const oldWidth = wasLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const oldHeight = wasLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
        const newWidth = isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const newHeight = isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;

        for (const item of this.items) {
            // Normalize position (0-1 range)
            const normalizedX = item.x / oldWidth;
            const normalizedY = item.y / oldHeight;

            // Rotate 90 degrees based on orientation change direction
            if (wasLandscape && !isLandscape) {
                // Landscape → Portrait
                item.x = normalizedY * newWidth;
                item.y = (1 - normalizedX) * newHeight;
            } else {
                // Portrait → Landscape
                item.x = (1 - normalizedY) * newWidth;
                item.y = normalizedX * newHeight;
            }
        }
    }

    render(ctx: CanvasRenderingContext2D, audioIntensity: number = 0): void {
        for (const item of this.items) {
            item.render(ctx, audioIntensity);
        }
    }
}
